export const convertAssetToMindMapData = (
  asset,
  vrTriggerOptions,
  manipulationTriggerOptions,
  assetModelOptions
) => {
  if (!asset) return null;

  const vrManipulation = asset.vr_manipulation || {};
  const events = asset.events || [];

  const vrEvents = (events || []).filter(
    (e) =>
      e?.action !== '__pivot_restore__' &&
      (e.vr_trigger || e.manipulation_trigger)
  );
  const vrEventsChildren =
    vrEvents.length > 0
      ? vrEvents.map((event, index) => ({
          name: `Event ${index + 1}`,
          eventData: event,
          children: [
            event.vr_trigger && {
              name: 'VR Trigger',
              value: event.vr_trigger,
              type: 'enum',
              options: vrTriggerOptions,
              path: `events.${index}.vr_trigger`,
              apiPath: 'events',
              eventId: event._id,
            },
            event.manipulation_trigger && {
              name: 'Manipulation Trigger',
              value: event.manipulation_trigger,
              type: 'enum',
              options: manipulationTriggerOptions,
              path: `events.${index}.manipulation_trigger`,
              apiPath: 'events',
              eventId: event._id,
            },
            event.action && {
              name: 'Action',
              value: event.action,
              type: 'text',
              path: `events.${index}.action`,
              apiPath: 'events',
              eventId: event._id,
            },
            event.affectedAsset && {
              name: 'Affected Asset',
              value: event.affectedAsset,
              type: 'text',
              path: `events.${index}.affectedAsset`,
              apiPath: 'events',
              eventId: event._id,
            },
            event.affectedAssetModel && {
              name: 'Asset Model',
              value: event.affectedAssetModel,
              type: 'enum',
              options: assetModelOptions,
              path: `events.${index}.affectedAssetModel`,
              apiPath: 'events',
              eventId: event._id,
            },
            event.url && {
              name: 'URL',
              value: event.url,
              type: 'text',
              path: `events.${index}.url`,
              apiPath: 'events',
              eventId: event._id,
            },
            event.manipulation_value_threshold != null && {
              name: 'Value Threshold',
              value: event.manipulation_value_threshold,
              type: 'number',
              min: 0,
              max: 10,
              path: `events.${index}.manipulation_value_threshold`,
              apiPath: 'events',
              eventId: event._id,
            },
          ].filter(Boolean),
        }))
      : [];

  // Helper to conditionally include sections
  const canGrab = vrManipulation.can_grab !== false;
  const canMove = vrManipulation.can_move !== false;
  const canRotate = vrManipulation.can_rotate !== false;
  const canScale = vrManipulation.can_scale !== false;
  const attachPoint =
    vrManipulation.grab_settings?.attach_point || 'contact_point';

  const grabSettingsSection = canGrab
    ? {
        name: 'Grab Settings',
        children: [
          {
            name: 'Grab Type',
            value: vrManipulation.grab_settings?.grab_type || 'snap',
            type: 'enum',
            options: ['none', 'snap', 'physics', 'kinematic', 'velocity'],
            path: 'vr_manipulation.grab_settings.grab_type',
            apiPath: 'manipulations/grab',
          },
          {
            name: 'Two Hand Grab',
            value: vrManipulation.grab_settings?.two_hand_grab ?? true,
            type: 'boolean',
            path: 'vr_manipulation.grab_settings.two_hand_grab',
            apiPath: 'manipulations/grab',
          },
          {
            name: 'Attach Point',
            value: attachPoint,
            type: 'enum',
            options: ['center', 'contact_point', 'custom'],
            path: 'vr_manipulation.grab_settings.attach_point',
            apiPath: 'manipulations/grab',
          },
          ...(attachPoint === 'custom'
            ? [
                {
                  name: 'Custom Attach Offset X',
                  value:
                    vrManipulation.grab_settings?.custom_attach_offset?.x ?? 0,
                  type: 'number',
                  min: -10,
                  max: 10,
                  path: 'vr_manipulation.grab_settings.custom_attach_offset.x',
                  apiPath: 'manipulations/grab',
                },
                {
                  name: 'Custom Attach Offset Y',
                  value:
                    vrManipulation.grab_settings?.custom_attach_offset?.y ?? 0,
                  type: 'number',
                  min: -10,
                  max: 10,
                  path: 'vr_manipulation.grab_settings.custom_attach_offset.y',
                  apiPath: 'manipulations/grab',
                },
                {
                  name: 'Custom Attach Offset Z',
                  value:
                    vrManipulation.grab_settings?.custom_attach_offset?.z ?? 0,
                  type: 'number',
                  min: -10,
                  max: 10,
                  path: 'vr_manipulation.grab_settings.custom_attach_offset.z',
                  apiPath: 'manipulations/grab',
                },
              ]
            : []),
          {
            name: 'Throw Multiplier',
            value: vrManipulation.grab_settings?.throw_multiplier ?? 1.5,
            type: 'number',
            min: 0,
            max: 5,
            path: 'vr_manipulation.grab_settings.throw_multiplier',
            apiPath: 'manipulations/grab',
          },
          {
            name: 'Angular Throw Multiplier',
            value: vrManipulation.grab_settings?.angular_throw_multiplier ?? 1,
            type: 'number',
            min: 0,
            max: 5,
            path: 'vr_manipulation.grab_settings.angular_throw_multiplier',
            apiPath: 'manipulations/grab',
          },
        ],
      }
    : null;

  const movementConstraintsSection = canMove
    ? {
        name: 'Movement Constraints',
        children: [
          {
            name: 'Lock X',
            value: vrManipulation.movement_constraints?.lock_x ?? false,
            type: 'boolean',
            path: 'vr_manipulation.movement_constraints.lock_x',
            apiPath: 'manipulations/constraints/movement',
          },
          {
            name: 'Lock Y',
            value: vrManipulation.movement_constraints?.lock_y ?? false,
            type: 'boolean',
            path: 'vr_manipulation.movement_constraints.lock_y',
            apiPath: 'manipulations/constraints/movement',
          },
          {
            name: 'Lock Z',
            value: vrManipulation.movement_constraints?.lock_z ?? false,
            type: 'boolean',
            path: 'vr_manipulation.movement_constraints.lock_z',
            apiPath: 'manipulations/constraints/movement',
          },
          {
            name: 'Snap to Grid',
            value: vrManipulation.movement_constraints?.snap_to_grid ?? false,
            type: 'boolean',
            path: 'vr_manipulation.movement_constraints.snap_to_grid',
            apiPath: 'manipulations/constraints/movement',
          },
          {
            name: 'Grid Size',
            value: vrManipulation.movement_constraints?.grid_size ?? 0.5,
            type: 'number',
            min: 0.1,
            max: 10,
            path: 'vr_manipulation.movement_constraints.grid_size',
            apiPath: 'manipulations/constraints/movement',
          },
          {
            name: 'Min X',
            value:
              vrManipulation.movement_constraints?.movement_range?.min_x ??
              null,
            type: 'number',
            min: -100,
            max: 100,
            path: 'vr_manipulation.movement_constraints.movement_range.min_x',
            apiPath: 'manipulations/constraints/movement',
          },
          {
            name: 'Max X',
            value:
              vrManipulation.movement_constraints?.movement_range?.max_x ??
              null,
            type: 'number',
            min: -100,
            max: 100,
            path: 'vr_manipulation.movement_constraints.movement_range.max_x',
            apiPath: 'manipulations/constraints/movement',
          },
          {
            name: 'Min Y',
            value:
              vrManipulation.movement_constraints?.movement_range?.min_y ??
              null,
            type: 'number',
            min: -100,
            max: 100,
            path: 'vr_manipulation.movement_constraints.movement_range.min_y',
            apiPath: 'manipulations/constraints/movement',
          },
          {
            name: 'Max Y',
            value:
              vrManipulation.movement_constraints?.movement_range?.max_y ??
              null,
            type: 'number',
            min: -100,
            max: 100,
            path: 'vr_manipulation.movement_constraints.movement_range.max_y',
            apiPath: 'manipulations/constraints/movement',
          },
          {
            name: 'Min Z',
            value:
              vrManipulation.movement_constraints?.movement_range?.min_z ??
              null,
            type: 'number',
            min: -100,
            max: 100,
            path: 'vr_manipulation.movement_constraints.movement_range.min_z',
            apiPath: 'manipulations/constraints/movement',
          },
          {
            name: 'Max Z',
            value:
              vrManipulation.movement_constraints?.movement_range?.max_z ??
              null,
            type: 'number',
            min: -100,
            max: 100,
            path: 'vr_manipulation.movement_constraints.movement_range.max_z',
            apiPath: 'manipulations/constraints/movement',
          },
        ],
      }
    : null;

  const rotationConstraintsSection = canRotate
    ? {
        name: 'Rotation Constraints',
        children: [
          {
            name: 'Lock X',
            value: vrManipulation.rotation_constraints?.lock_x ?? false,
            type: 'boolean',
            path: 'vr_manipulation.rotation_constraints.lock_x',
            apiPath: 'manipulations/constraints/rotation',
          },
          {
            name: 'Lock Y',
            value: vrManipulation.rotation_constraints?.lock_y ?? false,
            type: 'boolean',
            path: 'vr_manipulation.rotation_constraints.lock_y',
            apiPath: 'manipulations/constraints/rotation',
          },
          {
            name: 'Lock Z',
            value: vrManipulation.rotation_constraints?.lock_z ?? false,
            type: 'boolean',
            path: 'vr_manipulation.rotation_constraints.lock_z',
            apiPath: 'manipulations/constraints/rotation',
          },
          {
            name: 'Rotation Mode',
            value: vrManipulation.rotation_constraints?.rotation_mode || 'free',
            type: 'enum',
            options: ['free', 'snap', 'fixed_angles'],
            path: 'vr_manipulation.rotation_constraints.rotation_mode',
            apiPath: 'manipulations/constraints/rotation',
          },
          {
            name: 'Snap Angle',
            value: vrManipulation.rotation_constraints?.snap_angle ?? 15,
            type: 'number',
            min: 1,
            max: 90,
            path: 'vr_manipulation.rotation_constraints.snap_angle',
            apiPath: 'manipulations/constraints/rotation',
          },
        ],
      }
    : null;

  const scaleConstraintsSection = canScale
    ? {
        name: 'Scale Constraints',
        children: [
          {
            name: 'Uniform Scale',
            value: vrManipulation.scale_constraints?.uniform_scale ?? true,
            type: 'boolean',
            path: 'vr_manipulation.scale_constraints.uniform_scale',
            apiPath: 'manipulations/constraints/scale',
          },
          {
            name: 'Maintain Aspect',
            value:
              vrManipulation.scale_constraints?.maintain_aspect_ratio ?? true,
            type: 'boolean',
            path: 'vr_manipulation.scale_constraints.maintain_aspect_ratio',
            apiPath: 'manipulations/constraints/scale',
          },
          {
            name: 'Scale Sensitivity',
            value: vrManipulation.scale_constraints?.scale_sensitivity ?? 1,
            type: 'number',
            min: 0.1,
            max: 5,
            path: 'vr_manipulation.scale_constraints.scale_sensitivity',
            apiPath: 'manipulations/constraints/scale',
          },
          {
            name: 'Min Scale X',
            value: vrManipulation.scale_constraints?.min_scale?.x ?? 0.1,
            type: 'number',
            min: 0.01,
            max: 10,
            path: 'vr_manipulation.scale_constraints.min_scale.x',
            apiPath: 'manipulations/constraints/scale',
          },
          {
            name: 'Min Scale Y',
            value: vrManipulation.scale_constraints?.min_scale?.y ?? 0.1,
            type: 'number',
            min: 0.01,
            max: 10,
            path: 'vr_manipulation.scale_constraints.min_scale.y',
            apiPath: 'manipulations/constraints/scale',
          },
          {
            name: 'Min Scale Z',
            value: vrManipulation.scale_constraints?.min_scale?.z ?? 0.1,
            type: 'number',
            min: 0.01,
            max: 10,
            path: 'vr_manipulation.scale_constraints.min_scale.z',
            apiPath: 'manipulations/constraints/scale',
          },
          {
            name: 'Max Scale X',
            value: vrManipulation.scale_constraints?.max_scale?.x ?? 10,
            type: 'number',
            min: 0.1,
            max: 20,
            path: 'vr_manipulation.scale_constraints.max_scale.x',
            apiPath: 'manipulations/constraints/scale',
          },
          {
            name: 'Max Scale Y',
            value: vrManipulation.scale_constraints?.max_scale?.y ?? 10,
            type: 'number',
            min: 0.1,
            max: 20,
            path: 'vr_manipulation.scale_constraints.max_scale.y',
            apiPath: 'manipulations/constraints/scale',
          },
          {
            name: 'Max Scale Z',
            value: vrManipulation.scale_constraints?.max_scale?.z ?? 10,
            type: 'number',
            min: 0.1,
            max: 20,
            path: 'vr_manipulation.scale_constraints.max_scale.z',
            apiPath: 'manipulations/constraints/scale',
          },
        ],
      }
    : null;

  const permissionsChildren = [
    {
      name: 'Can Grab',
      value: vrManipulation.can_grab ?? true,
      type: 'boolean',
      path: 'vr_manipulation.can_grab',
      apiPath: 'manipulations',
      children: grabSettingsSection ? [grabSettingsSection] : undefined,
    },
    {
      name: 'Can Move',
      value: vrManipulation.can_move ?? true,
      type: 'boolean',
      path: 'vr_manipulation.can_move',
      apiPath: 'manipulations',
      children: movementConstraintsSection
        ? [movementConstraintsSection]
        : undefined,
    },
    {
      name: 'Can Rotate',
      value: vrManipulation.can_rotate ?? true,
      type: 'boolean',
      path: 'vr_manipulation.can_rotate',
      apiPath: 'manipulations',
      children: rotationConstraintsSection
        ? [rotationConstraintsSection]
        : undefined,
    },
    {
      name: 'Can Scale',
      value: vrManipulation.can_scale ?? true,
      type: 'boolean',
      path: 'vr_manipulation.can_scale',
      apiPath: 'manipulations',
      children: scaleConstraintsSection ? [scaleConstraintsSection] : undefined,
    },
  ];

  const manipulationChildren = [
    {
      name: 'Permissions',
      children: permissionsChildren,
    },
  ];

  return {
    name: asset.file_name || asset.asset_file_name || 'Asset',
    children: [
      {
        name: 'VR Manipulation',
        children: manipulationChildren,
      },
      ...(vrEventsChildren.length > 0
        ? [
            {
              name: 'VR Events',
              isEventsContainer: true,
              children: vrEventsChildren,
            },
          ]
        : [
            {
              name: 'VR Events',
              isEventsContainer: true,
              children: [],
            },
          ]),
    ],
  };
};
